import React, { useMemo, useRef } from "react";
import * as THREE from "three";

import { useFrame, useThree } from "@react-three/fiber";

import { Theme } from "./types";

interface EnhancedLightingProps {
  /** Overall brightness multiplier for all lights */
  brightness: number;
  /** Intensity of the rim light for edge highlighting */
  rimLightIntensity: number;
  /** Whether to enable shadow casting for directional lights */
  enableShadows: boolean;
  /** Theme for lighting color temperature */
  theme: Theme;
}

const SHADOW_MAP_SIZE = [2_048, 2_048] as const;

/**
 * Professional 3-point lighting system for 3D model visualization
 *
 * Implements a cinematography-standard lighting setup with theme adaptation:
 *
 * **Key Light**: Primary light source positioned at (2,2,2) relative to camera
 * - Provides main illumination and primary shadows
 * - High intensity (1.2x brightness) with mode-adaptive color temperature
 * - Casts high-quality 2048x2048 shadows when enabled
 *
 * **Fill Light**: Secondary light positioned at (-1,-1,1) relative to camera
 * - Softens shadows from the key light
 * - Lower intensity (0.4x brightness) with complementary color
 * - Provides ambient fill without casting additional shadows
 *
 * **Rim Light**: Accent light positioned at (0,1,-2) relative to camera
 * - Creates edge highlighting to separate subject from background
 * - Adjustable intensity with warm accent color
 * - Positioned behind subject for silhouette definition
 *
 * **Ambient Light**: Low-intensity environmental lighting
 * - Prevents completely black shadows
 * - Provides base illumination adapted to the theme
 *
 * All lights follow camera movement to maintain consistent lighting
 * regardless of viewing angle. Vector3 objects are memoized outside
 * the frame loop for optimal performance.
 *
 * @param props - Lighting configuration props
 * @returns JSX elements for the complete lighting setup
 */
export function EnhancedLighting({
  brightness,
  rimLightIntensity,
  enableShadows,
  theme,
}: EnhancedLightingProps) {
  const keyLightRef = useRef<THREE.DirectionalLight>(null);
  const fillLightRef = useRef<THREE.DirectionalLight>(null);
  const rimLightRef = useRef<THREE.DirectionalLight>(null);
  const { camera } = useThree();

  // Create Vector3 objects outside the frame loop for performance
  const keyLightPos = useMemo(() => new THREE.Vector3(), []);
  const fillLightPos = useMemo(() => new THREE.Vector3(), []);
  const rimLightPos = useMemo(() => new THREE.Vector3(), []);

  // Light colors based on theme
  const lightColors = useMemo(() => {
    if (theme === "dark") {
      return {
        ambient: 0x40_40_60, // Cool dark ambient
        key: 0xff_ff_ff, // Neutral white key
        fill: 0x60_80_ff, // Cool blue fill
        rim: 0xff_c0_60, // Warm orange rim
      };
    } else {
      return {
        ambient: 0xf0_f0_ff, // Warm light ambient
        key: 0xff_ff_f0, // Warm white key
        fill: 0xc0_d0_ff, // Cool light fill
        rim: 0xff_e0_a0, // Warm yellow rim
      };
    }
  }, [theme]);

  /**
   * Updates light positions every frame to follow camera movement
   * Ensures consistent lighting regardless of viewing angle
   */
  useFrame(() => {
    if (keyLightRef.current && fillLightRef.current && rimLightRef.current) {
      // Key light: Primary illumination from top-right-front
      keyLightPos
        .set(3, 2.5, 3)
        .applyQuaternion(camera.quaternion)
        .add(camera.position);
      keyLightRef.current.position.copy(keyLightPos);
      keyLightRef.current.target.position.copy(camera.position);
      keyLightRef.current.target.updateMatrixWorld();

      // Fill light: Secondary illumination from bottom-left-front
      fillLightPos
        .set(-1, -1, 1)
        .applyQuaternion(camera.quaternion)
        .add(camera.position);
      fillLightRef.current.position.copy(fillLightPos);

      // Rim light: Edge highlighting from top-back
      rimLightPos
        .set(0, 1, -2)
        .applyQuaternion(camera.quaternion)
        .add(camera.position);
      rimLightRef.current.position.copy(rimLightPos);
    }
  });

  return (
    <>
      {/* Base ambient lighting adapted to the selected theme */}
      <ambientLight intensity={0.3 * brightness} color={lightColors.ambient} />

      {/* Key Light: Primary directional light with diffuse shadows */}
      <directionalLight
        ref={keyLightRef}
        intensity={1.2 * brightness}
        color={lightColors.key}
        castShadow={enableShadows}
        shadow-mapSize={SHADOW_MAP_SIZE}
        shadow-camera-near={0.1}
        shadow-camera-far={20}
        shadow-camera-left={-15}
        shadow-camera-right={15}
        shadow-camera-top={15}
        shadow-camera-bottom={-15}
        shadow-bias={-0.000_1}
        shadow-radius={15}
        shadow-blurSamples={35}
      />

      {/* Fill Light: Secondary light with complementary color */}
      <directionalLight
        ref={fillLightRef}
        intensity={0.4 * brightness}
        color={lightColors.fill}
      />

      {/* Rim Light: Edge highlighting with warm accent */}
      <directionalLight
        ref={rimLightRef}
        intensity={rimLightIntensity}
        color={lightColors.rim}
      />
    </>
  );
}
